import React, { useState, useEffect } from "react";
import SearchComponent from "../Custom Components/Search comonent/searchComponent";
import CustomButton from "../Custom Components/CustomButton/customButton";
import CustomTableOfDashboard from "../Custom Components/CustomTable/CustomTableOfDashboard";
import UpdateModalForm from "../updateModalForm/updateModalForm";
import ConfirmationModal from "../Confirmation Modal/confirmationModal";
import AddEmployeeModal from "../AddEmployeeModal/AddEmployeeModal";
import ArchiveEmployeeModal from "../Archive/ArchiveEmployeeModal";
import ImportEmployeesModal from "../ImportEmployees/ImportEmployeesModal";
import { axios } from "../../lib/axios";
import { useAuthContext } from "../../contexts/AuthContext";
import { invalidateEmployees } from "../../lib/employees";
import { formatPayroll } from "../../lib/currency";
import { t } from "../../i18n";
import { errorMessage } from "../../lib/download";

export const DashboardPage = () => {
  const { checkAuth, isOwner } = useAuthContext();
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  const [workerToArchive, setWorkerToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [workersPerPage] = useState(10);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  // Fetch workers data from the backend
  const fetchWorkers = async () => {
    try {
      // Check authentication before making API calls
      if (!checkAuth()) {
        return;
      }

      setLoading(true);
      const response = await axios.get("/employees", { params: { status: statusFilter } });
      setWorkers(response.data);
      setError("");
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch workers");
      setLoading(false);
      // The axios interceptor will handle 401 responses automatically
    }
  };

  // Archive: keeps every record, only removes the employee from the active lists.
  const archiveWorker = async (details) => {
    setArchiving(true);
    try {
      const { data } = await axios.post(`/employees/${workerToArchive.id}/archive`, details);
      invalidateEmployees();
      setWorkerToArchive(null);
      const owed = Number(data.outstanding?.unpaid_balance || 0);
      const deductions = Number(data.outstanding?.outstanding_deductions || 0);
      setConfirmationMessage(
        owed > 0 || deductions > 0
          ? t(
              "تمت الأرشفة. ما زال له رصيد غير مدفوع {owed} وخصومات قائمة {deductions}؛ يمكن تسويتها من تقرير الرواتب وقسم الاستقطاع.",
              { owed: formatPayroll(owed), deductions: formatPayroll(deductions) }
            )
          : t("تمت أرشفة الموظف. بقيت سجلاته كما هي.")
      );
      setIsSuccessModalOpen(true);
      fetchWorkers();
    } catch (err) {
      setWorkerToArchive(null);
      setConfirmationMessage(await errorMessage(err));
      setIsSuccessModalOpen(true);
    } finally {
      setArchiving(false);
    }
  };

  // Restore (owners only; the server checks too).
  const restoreWorker = async (worker) => {
    try {
      await axios.post(`/employees/${worker.id}/restore`);
      invalidateEmployees();
      setConfirmationMessage(t("تمت استعادة الموظف إلى القائمة النشطة."));
      fetchWorkers();
    } catch (err) {
      setConfirmationMessage(await errorMessage(err));
    }
    setIsSuccessModalOpen(true);
  };

  // Add new employee
  const addWorker = async (newWorkerDetails) => {
    try {
      // Check authentication before making API calls
      if (!checkAuth()) {
        return;
      }

      const response = await axios.post("/employees", newWorkerDetails);
      invalidateEmployees();
      if (statusFilter !== "archived") setWorkers([...workers, response.data]);
      setIsAddModalOpen(false);
      setConfirmationMessage(t("تم إضافة الموظف بنجاح!"));
      setIsSuccessModalOpen(true);
    } catch (err) {
      setConfirmationMessage(
        t("فشل في إضافة الموظف: ") + (err.response?.data?.message || t("حدث خطأ ما"))
      );
      setIsSuccessModalOpen(true);
      // The axios interceptor will handle 401 responses automatically
    }
  };

  // Delete worker
  const deleteWorker = async (workerId) => {
    try {
      // Check authentication before making API calls
      if (!checkAuth()) {
        return;
      }

      await axios.delete(`/employees/${workerId}`);
      invalidateEmployees();
      setWorkers(workers.filter((worker) => worker.id !== workerId));
      setIsDeleteConfirmationOpen(false);
      setConfirmationMessage(t("تم حذف الموظف بنجاح!"));
      setIsSuccessModalOpen(true);
    } catch (err) {
      setIsDeleteConfirmationOpen(false);
      setConfirmationMessage(
        t("فشل في حذف الموظف: ") + (t(err.response?.data?.message) || t("حدث خطأ ما"))
      );
      setIsSuccessModalOpen(true);
      // The axios interceptor will handle 401 responses automatically
    }
  };

  // Update worker
  const updateWorker = async (workerId, updatedDetails) => {
    try {
      // Check authentication before making API calls
      if (!checkAuth()) {
        return;
      }

      const response = await axios.put(`/employees/${workerId}`, updatedDetails);
      invalidateEmployees();
      setWorkers(
        workers.map((worker) => (worker.id === workerId ? { ...worker, ...response.data } : worker))
      );
      setIsModalOpen(false);
      setConfirmationMessage(t("تم تحديث بيانات الموظف بنجاح!"));
      setIsSuccessModalOpen(true);
    } catch (err) {
      setConfirmationMessage(
        t("فشل في تحديث بيانات الموظف: ") + (err.response?.data?.message || t("حدث خطأ ما"))
      );
      setIsSuccessModalOpen(true);
      // The axios interceptor will handle 401 responses automatically
    }
  };

  // Open delete confirmation modal
  const confirmDeleteWorker = (worker) => {
    setWorkerToDelete(worker);
    setIsDeleteConfirmationOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (workerToDelete) {
      deleteWorker(workerToDelete.id);
    }
  };

  // Filter workers based on the search input (Arabic-friendly)
  const filteredWorkers = workers.filter((worker) => {
    const normalizedSearch = search.trim().toLowerCase();
    const normalizedName = worker.name.trim().toLowerCase();
    const normalizedJob = worker.job.trim().toLowerCase();
    return normalizedName.includes(normalizedSearch) || normalizedJob.includes(normalizedSearch);
  });

  // Pagination logic
  const indexOfLastWorker = currentPage * workersPerPage;
  const indexOfFirstWorker = indexOfLastWorker - workersPerPage;
  const currentWorkers = filteredWorkers.slice(indexOfFirstWorker, indexOfLastWorker);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    fetchWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Reset to the first page whenever the search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Custom render data
  const tableData = currentWorkers.map((worker, index) => ({
    sequence: indexOfFirstWorker + index + 1,
    id: worker.id,
    name: worker.status === "archived" ? `${worker.name} — ${t("مؤرشف")}` : worker.name,
    job: worker.job,
    salary: formatPayroll(worker.salary),
    phone: worker.phone,
    _archived: worker.status === "archived", // "_" fields are not shown as columns
  }));

  // Actions. `hidden` keeps edit/archive/delete for active rows and restore (owners) for archived ones.
  const actions = [
    {
      label: t("تعديل"),
      className: "btn-action text-brand-700 hover:bg-brand-50 hover:underline underline-offset-4",
      hidden: (row) => row._archived,
      onClick: (worker) => {
        setSelectedWorker(worker);
        setIsModalOpen(true);
      },
    },
    {
      label: t("أرشفة"),
      className: "btn-action text-amber-800 hover:bg-amber-50 hover:underline underline-offset-4",
      hidden: (row) => row._archived,
      onClick: (row) => setWorkerToArchive(workers.find((w) => w.id === row.id)),
    },
    {
      label: t("حذف"),
      className: "btn-action text-red-700 hover:bg-red-50 hover:underline underline-offset-4",
      hidden: (row) => row._archived,
      onClick: (worker) => confirmDeleteWorker(worker),
    },
    {
      label: t("استعادة"),
      className: "btn-action text-brand-700 hover:bg-brand-50 hover:underline underline-offset-4",
      hidden: (row) => !row._archived || !isOwner,
      onClick: (row) => restoreWorker(workers.find((w) => w.id === row.id)),
    },
  ];

  // Table headers
  const headers = [
    t("التسلسل"),
    t("الاسم"),
    t("الوظيفة"),
    t("الراتب"),
    t("الهاتف"),
    t("الإجراءات"),
  ];

  return (
    <>
      {/* Content Section */}
      <div>
        {/* Search and Add Worker */}
        <div className="card-toolbar">
          {/* Active / archived / all */}
          <div
            role="group"
            aria-label={t("حالة الموظفين")}
            className="flex gap-1 rounded-xl bg-slate-100 p-1"
          >
            {[
              ["active", "نشط"],
              ["archived", "مؤرشف"],
              ["all", "الكل"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={statusFilter === value}
                onClick={() => setStatusFilter(value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  statusFilter === value
                    ? "bg-card text-brand-700 shadow-sm"
                    : "text-slate-600 hover:text-brand-700"
                }`}
              >
                {t(label)}
              </button>
            ))}
          </div>

          {/* Import (owners) and Add Employee */}
          <div className="flex items-center gap-2">
            <CustomButton text={t("اضافة عامل جديد")} onClick={() => setIsAddModalOpen(true)} />
            {isOwner && (
              <button
                type="button"
                onClick={() => setIsImportOpen(true)}
                className="rounded-xl border border-slate-300 bg-card px-4 py-2.5 font-medium text-slate-700 shadow-sm hover:bg-slate-100"
              >
                {t("استيراد موظفين")}
              </button>
            )}
          </div>
          {/* Search Bar */}
          <div className="w-full sm:ms-auto sm:w-auto sm:min-w-[20rem]">
            <SearchComponent setSearch={setSearch} />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center mt-6">Loading...</div>
        ) : error ? (
          <div className="text-center mt-6 text-red-600">{error}</div>
        ) : (
          <CustomTableOfDashboard headers={headers} data={tableData} actions={actions} />
        )}

        {/* Pagination */}
        <div className="flex justify-center mt-4">
          {[...Array(Math.ceil(filteredWorkers.length / workersPerPage)).keys()].map(
            (pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => paginate(pageNumber + 1)}
                className={`px-4 py-2 mx-1 ${
                  currentPage === pageNumber + 1
                    ? "bg-brand-600 text-white shadow-sm"
                    : "bg-card text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100"
                } rounded-lg font-medium hover:bg-brand-700 hover:text-white`}
              >
                {pageNumber + 1}
              </button>
            )
          )}
        </div>
      </div>

      {/* Add Employee Modal */}
      {isAddModalOpen && (
        <AddEmployeeModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={addWorker}
        />
      )}

      {/* Update Modal */}
      {isModalOpen && (
        <UpdateModalForm
          isOpen={isModalOpen}
          worker={selectedWorker}
          onClose={() => setIsModalOpen(false)}
          onSave={(updatedDetails) => updateWorker(selectedWorker.id, updatedDetails)}
        />
      )}

      {workerToArchive && (
        <ArchiveEmployeeModal
          employee={workerToArchive}
          busy={archiving}
          onClose={() => setWorkerToArchive(null)}
          onConfirm={archiveWorker}
        />
      )}

      {isImportOpen && (
        <ImportEmployeesModal
          onClose={() => setIsImportOpen(false)}
          onImported={() => {
            invalidateEmployees();
            fetchWorkers();
          }}
        />
      )}

      {/* Success Alert Modal */}
      <ConfirmationModal
        isOpen={isSuccessModalOpen}
        message={confirmationMessage}
        onConfirm={() => setIsSuccessModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        message={t('هل أنت متأكد أنك تريد حذف العامل "{name}"؟', { name: workerToDelete?.name })}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteConfirmationOpen(false)}
      />
    </>
  );
};

export default DashboardPage;
